<template>
  <main role="main">
    <b-container class="py-4">
      <h4>{{ $t("editEvent") }}</h4>

      <b-form id="new-calendar-form" @submit.prevent="submitForm">
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3">
            <label class="my-0">{{ $t("datetime") }} <span class="text-danger">*</span></label>
          </b-col>
          <b-col cols="9">
            <b-form-row class="mb-2">
              <b-col cols="4" class="d-flex flex-row align-items-center">
                <label for="start-date-picker" class="datetime-label flex-shrink-0 my-0 mr-3">{{ $t("startDate") }}</label>
                <b-form-datepicker id="start-date-picker" v-model="startDate" required
                                   no-flip hide-header
                                   label-no-date-selected="DD/MM/YYYY"
                                   locale="vi"
                                   :date-format-options="datePickerFormatOptions"
                                   start-weekday="1"></b-form-datepicker>
              </b-col>
              <b-col cols="1"></b-col>
              <b-col cols="4" class="d-flex flex-row align-items-center">
                <label for="start-time-picker" class="datetime-label flex-shrink-0 my-0 mr-3">{{ $t("eventStartTime") }}</label>
                <b-select id="start-time-picker" :options="timeOptions" v-model="startTime"></b-select>
              </b-col>
              <b-col cols="3" class="d-flex flex-row align-items-center justify-content-end">
                <b-form-checkbox v-model="isAllDay">{{ $t("eventAllDay") }}</b-form-checkbox>
              </b-col>
            </b-form-row>
            <b-form-row v-if="!isAllDay">
              <b-col cols="4" class="d-flex flex-row align-items-center">
                <label for="end-date-picker" class="datetime-label flex-shrink-0 my-0 mr-3">{{ $t("endDate") }}</label>
                <b-form-datepicker id="end-date-picker" v-model="endDate" required
                                   no-flip hide-header
                                   label-no-date-selected="DD/MM/YYYY"
                                   locale="vi"
                                   :date-format-options="datePickerFormatOptions"
                                   start-weekday="1"></b-form-datepicker>
              </b-col>
              <b-col cols="1"></b-col>
              <b-col cols="4" class="d-flex flex-row align-items-center">
                <label for="end-time-picker" class="datetime-label flex-shrink-0 my-0 mr-3">{{ $t("eventEndTime") }}</label>
                <b-select id="end-time-picker" :options="timeOptions" v-model="endTime"></b-select>
              </b-col>
            </b-form-row>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="input-name">{{ $t("content") }} <span class="text-danger">*</span></label></b-col>
          <b-col cols="9">
            <b-form-textarea id="input-name" v-model="subject" required minlength="2" maxlength="500"/>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="input-chairman">{{ $t("chairman") }}</label></b-col>
          <b-col cols="9">
            <b-form-input id="input-chairman" type="text" v-model="chairman" minlength="2" maxlength="250"/>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="input-preparation">{{ $t("eventPreparation") }}</label></b-col>
          <b-col cols="9">
            <b-form-input id="input-preparation" type="text" v-model="meetingPreparation" minlength="2" maxlength="250"/>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="input-participants">{{ $t("eventParticipants") }}</label></b-col>
          <b-col cols="9">
            <b-form-input id="input-participants" type="text" v-model="participants" minlength="2" maxlength="250"/>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="input-invitees">{{ $t("eventInvitees") }}</label></b-col>
          <b-col cols="9">
            <b-form-input id="input-invitees" type="text" v-model="invitees" minlength="2" maxlength="250"/>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="input-location">{{ $t("location") }}</label></b-col>
          <b-col cols="9">
            <v-select v-if="!locationId" label="name" class="vs-normalizer"
                      :clearable="false"
                      :filterable="false"
                      :options="typeaheadOptions"
                      :clearSearchOnBlur="() => false"
                      :clearSearchOnSelect="false"
                      @search="doSearchDebounced" @option:selecting="onSelecting">
              <template v-slot:search="{attributes, events}">
                <input id="input-name" v-bind="attributes" v-on="events" v-model="location"
                       class="form-control" minlength="2" maxlength="100"/>
              </template>
            </v-select>
            <b-input-group v-else>
              <b-form-input type="text" readonly v-model="location" />
              <b-input-group-append>
                <b-input-group-text class="cursor-pointer" @click="clearSelectedLocation">
                  <b-icon-x />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="input-description">{{ $t("note") }} <span class="text-danger">*</span></label></b-col>
          <b-col cols="9">
            <b-form-textarea id="input-description" v-model="description" required minlength="2" maxlength="500"/>
          </b-col>
        </b-form-row>
        <b-form-row class="align-items-center mb-3">
          <b-col cols="3">
            <label for="input-group">Đơn vị:</label>
          </b-col>
          <b-col cols="9">
            <b-form-select v-model="selectedGroup" :options="listGroupOptions" value-field="id" text-field="name">
              <option value="" >-- Chọn đơn vị --</option>
            </b-form-select>
          </b-col>
        </b-form-row>
        <!--<b-form-row class="align-items-center mb-3">
          <b-col cols="3"><label for="radio-status">{{ $t("status") }}</label></b-col>
          <b-col cols="9">
            <b-form-radio-group id="radio-status" v-model="status" :options="statusOptions" />
          </b-col>
        </b-form-row>-->
        <div v-if="formErrorMsg" class="text-center text-danger mb-4">{{ $t(formErrorMsg) }}</div>
        <div class="text-center my-4">
          <b-button type="submit" variant="primary" :disabled="isSubmitting" @click="beforeFormValidation">Lưu</b-button>
        </div>
      </b-form>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
  .datetime-label {
    width: 100px;
  }
</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import {DateTime, Duration} from "luxon";
  import BaseComponent from "@/components/BaseComponent";
  import * as calendarService from "@/service/calendarService";

  @Component
  export default class EditCalendar extends BaseComponent {

    readonly datePickerFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
    readonly statusOptions = [
      { text: "lịch đăng ký mới chưa duyệt (0)", value: 0 },
      { text: "lịch bị hủy (2)", value: 2 },
      { text: "lịch đã được duyệt (4)", value: 4 },
    ];

    calendar = null;

    startDate = ""; // yyyy-MM-dd
    startTime = ""; // HH:mm
    endDate = ""; // yyyy-MM-dd
    endTime = ""; // HH:mm
    timeOptions = [];
    isAllDay = false;

    subject = "";
    chairman = "";
    meetingPreparation = "";
    participants = "";
    invitees = "";
    locationId = 0;
    location = "";
    description = "";
    status = 0;

    selectedGroup="";
    listGroupOptions = [];

    typeaheadOptions = [];
    doSearchDebounced: Function = _.noop;

    formErrorMsg = "";
    isSubmitting = false;

    mounted() {
      this.loadGroupList();
      this.restoreFormData();

      this.timeOptions = [];
      for (let i = 0; i < 1440; i += 15) {
        const value = DateTime.now().startOf("day").plus({ minutes: i }).toFormat("HH:mm");
        this.timeOptions.push(value);
      }

      this.doSearchDebounced = _.debounce(async (q: string, loading: (p: boolean) => void) => {
        try {
          if (q.length < 2) {
            this.typeaheadOptions = [];
          } else {
            loading(true);
            this.typeaheadOptions = (await calendarService.getLocations({ q, page: 1, pageSize: 20 })).data;
          }

        } catch (e) {
          this.toastError(e);

        } finally {
          loading(false);
        }
      }, 300);
    }

    restoreFormData() {
      this.calendar = JSON.parse(sessionStorage.getItem("editCalendar"));

      const startAt = DateTime.fromISO(this.calendar.startAt);
      this.startDate = startAt.toFormat("yyyy-MM-dd");
      this.startTime = startAt.toFormat("HH:mm");

      if (this.calendar.endAt) {
        const endAt = DateTime.fromISO(this.calendar.endAt);
        this.endDate = endAt.toFormat("yyyy-MM-dd");
        this.endTime = endAt.toFormat("HH:mm");
        this.isAllDay = false;
      } else {
        this.isAllDay = true;
      }

      this.subject = this.calendar.subject;
      this.chairman = this.calendar.chairman;
      this.meetingPreparation = this.calendar.meetingPreparation;
      this.participants = this.calendar.participants;
      this.invitees = this.calendar.invitees;
      this.locationId = this.calendar.locationId;
      this.location = this.calendar.location;
      this.description = this.calendar.description;
      this.status = this.calendar.status;
      this.selectedGroup=(this.calendar.groupId==null)?"":this.calendar.groupId;
    }

    onSelecting(option) {
      this.typeaheadOptions = [];
      this.locationId = option.id;
      this.location = option.name;
    }

    clearSelectedLocation() {
      this.locationId = undefined;
      this.location = "";
    }

    beforeFormValidation() {
      this.subject = this.subject.trim();
      this.chairman = this.chairman.trim();
      this.meetingPreparation = this.meetingPreparation.trim();
      this.participants = this.participants.trim();
      this.invitees = this.invitees.trim();
      this.location = this.location.trim();
      this.description = this.description.trim();
    }

    async submitForm() {
      this.formErrorMsg = "";

      const startDateTime = DateTime.fromFormat(`${this.startDate} ${this.startTime}`, "yyyy-MM-dd HH:mm");
      const endDateTime = DateTime.fromFormat(`${this.endDate} ${this.endTime}`, "yyyy-MM-dd HH:mm");

      /*if (startMoment.diff(moment()) < 0) {
        this.formErrorMsg = "Thời gian bắt đầu không hợp lệ";
        return;
      }*/

      if (!this.isAllDay && endDateTime.diff(startDateTime).as("minutes") < 15) {
        this.formErrorMsg = "timeEndValid";
        return;
      }

      try {
        this.isSubmitting = true;
        await calendarService.editCalendar({
          id: this.calendar.id,
          subject: this.subject.trim(),
          description: this.description.trim(),
          chairman: this.chairman.trim(),
          startAt: startDateTime.toISO(),
          endAt: this.isAllDay? undefined : endDateTime.toISO(),
          meetingPreparation: this.meetingPreparation.trim(),
          invitees: this.invitees.trim(),
          location: this.location.trim(),
          locationId: this.locationId || undefined,
          groupId:+this.selectedGroup,
          participants: this.participants.trim(),
          status: this.status
        });
        this.$router.push("/calendar");
        this.toastSuccessDelay("Cập nhật lịch thành công");

      } catch (e) {
        this.formErrorMsg = e.response?.data?.code || e.code;
        this.toastError(e);

      } finally {
        this.isSubmitting = false;
      }
    }

    async loadGroupList(){
      const listGroupOptions =await calendarService.getAllGroups();
      this.listGroupOptions=listGroupOptions;
    }


  }
</script>
